/**
 * Milestone Timeline for Conditions Portal
 *
 * Displays loan milestone progress as a vertical timeline in the left sidebar.
 * Uses the flex-column icon track pattern (Preline UI) — dot and line are siblings
 * in the same flex column, auto-aligned via items-center. No absolute positioning.
 *
 * Shows Pacific time dates, elapsed time between milestones, and total days at top.
 */

import { CheckCircle2, Circle } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { MilestoneHistoryItem } from '@/components/encompass/prospects/types';

// Format a date string to Pacific time: "M/D/YY h:mm A PT"
function formatPacificDate(dateString: string | null | undefined): string {
  if (!dateString) {
return '-';
}
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
return '-';
}
    return `${date.toLocaleString('en-US', {
      timeZone: 'America/Los_Angeles',
      month: 'numeric',
      day: 'numeric',
      year: '2-digit',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })  } PT`;
  } catch {
    return '-';
  }
}

// Calculate days between two date strings
function daysBetween(startDate: string | null | undefined, endDate: string | null | undefined): number | null {
  if (!startDate || !endDate) {
return null;
}
  try {
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
return null;
}
    // Calendar day difference — strip time, count date boundaries
    const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    return Math.round((endDay.getTime() - startDay.getTime()) / (1000 * 60 * 60 * 24));
  } catch {
    return null;
  }
}

// Calculate total elapsed days from first completed milestone to now (or funded date)
function calculateTotalDays(milestones: MilestoneHistoryItem[]): number | null {
  const firstDone = milestones.find(m => m.done && m.doneDate)?.doneDate;
  if (!firstDone) {
return null;
}

  // Keep counting until funded — use funded date if done, otherwise today
  const fundedMilestone = milestones.find(m => m.name === 'Funded' && m.done && m.doneDate);

  const start = new Date(firstDone);
  const end = fundedMilestone ? new Date(fundedMilestone.doneDate!) : new Date();
  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
return null;
}
  // Calendar day difference — strip time, count date boundaries
  const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  return Math.round((endDay.getTime() - startDay.getTime()) / (1000 * 60 * 60 * 24));
}

interface MilestoneTimelineProps {
  milestones: MilestoneHistoryItem[];
  currentMilestone?: string | null;
}

export function MilestoneTimeline({ milestones }: MilestoneTimelineProps) {
  if (!milestones || milestones.length === 0) {
return null;
}

  const totalDays = calculateTotalDays(milestones);

  return (
    <div>
      {/* Header with total days */}
      <div className="mb-3">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Timeline</h3>
        {totalDays !== null && (
          <span className="text-xs font-medium text-muted-foreground">
            {totalDays} day{totalDays !== 1 ? 's' : ''} total
          </span>
        )}
      </div>

      {/* Timeline — flex-column icon track pattern */}
      <div>
        {milestones.map((milestone, index) => {
          const isDone = milestone.done === true;
          const isLast = index === milestones.length - 1;
          // Line is green only when both this AND next milestone are done
          const lineGreen = isDone && milestones[index + 1]?.done === true;

          // Elapsed days from previous done milestone
          const prevMilestone = index > 0 ? milestones[index - 1] : null;
          const isLastDone = isDone && milestones[index + 1]?.done !== true;
          const isFunded = milestones.some(m => m.name === 'Funded' && m.done);
          let elapsedDays: number | null = null;
          if (isLastDone && !isFunded && milestone.doneDate) {
            // Last completed milestone: show days since completion (continuing count)
            elapsedDays = daysBetween(milestone.doneDate, new Date().toISOString());
          } else if (isDone && prevMilestone?.done === true && prevMilestone?.doneDate && milestone.doneDate) {
            elapsedDays = daysBetween(prevMilestone.doneDate, milestone.doneDate);
          }

          return (
            <div key={milestone.id || index} className="flex gap-x-3">
              {/* Icon track — dot + line in a flex column */}
              <div className="flex flex-col items-center">
                {/* Dot */}
                <div className="shrink-0">
                  {isDone ? (
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                  ) : (
                    <Circle className="h-4 w-4 text-muted-foreground/30" />
                  )}
                </div>
                {/* Line below — fills remaining height, stops at bottom of content */}
                {!isLast && (
                  <div className={cn(
                    'w-0.5 flex-1 mt-1',
                    lineGreen ? 'bg-green-500' : 'bg-muted-foreground/15'
                  )} />
                )}
              </div>

              {/* Content */}
              <div className={cn('pb-5 min-w-0', !isDone && 'opacity-40')}>
                <span className={cn(
                  'text-sm leading-none',
                  isDone ? 'font-medium text-gray-900' : 'text-gray-500'
                )}>
                  {milestone.name}
                </span>

                {/* Date — only for completed milestones */}
                {isDone && milestone.doneDate && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {formatPacificDate(milestone.doneDate)}
                  </p>
                )}

                {/* Elapsed days from previous milestone */}
                {elapsedDays !== null && elapsedDays > 0 && (
                  <span className="inline-block text-[10px] text-muted-foreground bg-muted/70 px-1.5 py-0.5 rounded mt-1">
                    {elapsedDays} day{elapsedDays !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default MilestoneTimeline;
